import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  TextField,
} from "@mui/material";

// ダミーデータ型（必要に応じて拡張）
type UserReportItem = {
  id: string | number;
  parentUser: string;
  childUser: string;
  counts: number[]; // 単品〜破棄の10個の数値
};

type Props = {
  data: UserReportItem[];
  checkedIds: Set<string>;
  onCheckedChange: (id: string) => void;
  onCheckedChangeAll: (checkedIds: Set<string>) => void;
};

const COLUMN_TITLES = [
  "単品",
  "大型",
  "要・危",
  "シール",
  "セット",
  "商品未入力",
  "SKU手数料",
  "JAN違い",
  "返送",
  "破棄",
];

const UserReportTable: React.FC<Props> = ({
  data,
  checkedIds,
  onCheckedChange,
  onCheckedChangeAll,
}) => {
  const [filters, setFilters] = useState({
    parentUser: "",
    childUser: "",
    counts: Array(COLUMN_TITLES.length).fill(""),
  });

  const handleFilterChange = (key: string, value: string, index?: number) => {
    if (key === "counts" && typeof index === "number") {
      const newCounts = [...filters.counts];
      newCounts[index] = value;
      setFilters({ ...filters, counts: newCounts });
    } else {
      setFilters({ ...filters, [key]: value });
    }
  };

  const filteredData = data.filter((item) => {
    const parentMatch = item.parentUser.includes(filters.parentUser);
    const childMatch = item.childUser.includes(filters.childUser);
    const countsMatch = filters.counts.every((filter, idx) => {
      return filter === "" || String(item.counts[idx]).includes(filter);
    });
    return parentMatch && childMatch && countsMatch;
  });

  return (
    <TableContainer
      component={Paper}
      sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell rowSpan={2} align="center">
              <Checkbox
                checked={
                  checkedIds.size === filteredData.length &&
                  filteredData.length > 0
                }
                indeterminate={
                  checkedIds.size > 0 && checkedIds.size < filteredData.length
                }
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) {
                    const newSet = new Set(
                      filteredData.map((item) => String(item.id))
                    );
                    onCheckedChangeAll(newSet);
                  } else {
                    onCheckedChangeAll(new Set());
                  }
                }}
              />
            </TableCell>
            <TableCell rowSpan={2} align="center">
              No
            </TableCell>
            <TableCell rowSpan={2} align="center">
              ユーザー
            </TableCell>
            <TableCell rowSpan={2} align="center">
              ユーザー（子）
            </TableCell>
            <TableCell colSpan={10} align="center">
              梱包件数
            </TableCell>
          </TableRow>
          <TableRow>
            {COLUMN_TITLES.map((title, idx) => (
              <TableCell key={idx} align="center">
                {title}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell />
            <TableCell />
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) =>
                  handleFilterChange("parentUser", e.target.value)
                }
              />
            </TableCell>
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) =>
                  handleFilterChange("childUser", e.target.value)
                }
              />
            </TableCell>
            {COLUMN_TITLES.map((_, idx) => (
              <TableCell key={idx}>
                <TextField
                  variant="standard"
                  placeholder="フィルター"
                  fullWidth
                  onChange={(e) =>
                    handleFilterChange("counts", e.target.value, idx)
                  }
                />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredData.map((row, idx) => (
            <TableRow key={row.id}>
              <TableCell align="center">
                <Checkbox
                  checked={checkedIds.has(String(row.id))}
                  onChange={(e) => onCheckedChange(String(row.id))}
                />
              </TableCell>
              <TableCell align="center">{idx + 1}</TableCell>
              <TableCell>{row.parentUser}</TableCell>
              <TableCell>{row.childUser}</TableCell>
              {row.counts.map((count, i) => (
                <TableCell key={i} align="center">
                  {count}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UserReportTable;
